import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  flexRender,
} from '@tanstack/react-table'
import { H3 } from '@/components/sv/typographies'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const columns: ColumnDef<unknown>[] = [
  {
    accessorKey: 'source',
    header: () => <Skeleton className="ml-[38px] pl-6 h-6 w-18" />,
    cell: () => (
      <div className="flex items-center gap-2">
        <div className="grid place-items-center px-2">
          <Skeleton className="size-4" />
        </div>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-24" />
          <div className="flex items-center gap-2">
            <Skeleton className="size-4" />
            <Skeleton className="rounded-sm h-4 w-24" />
            <Skeleton className="rounded-sm h-4 w-18" />
          </div>
        </div>
      </div>
    ),
    size: 450,
  },
  {
    accessorKey: 'trigger',
    header: () => <Skeleton className="h-6 w-12" />,
    size: 200,
    cell: () => (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-32" />
        <div className="flex items-center gap-2">
          <Skeleton className="size-4" />
          <Skeleton className="h-4 w-18" />
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: () => (
      <div className="flex justify-end">
        <Skeleton className="ml-[16px] pl-6 h-6 w-24" />
      </div>
    ),
    cell: () => (
      <div className="flex flex-col gap-2 items-end">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-16" />
      </div>
    ),
    size: 200,
  },
]

export const StaticSiteRecentDeploymentsFallback = () => {
  const table = useReactTable({
    data: new Array(3).fill({}),
    columns,
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="pb-25">
      <Card>
        <CardHeader className="w-full gap-0">
          <CardTitle>
            <H3>Recent deployments</H3>
          </CardTitle>
          <div className="flex items-center justify-between w-full">
            <CardAction className="flex w-full gap-2 justify-end">
              <Skeleton className="h-9 w-31.5" />
            </CardAction>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border pb-0.5">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-muted/50 hover:bg-muted/50"
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          style={{ maxWidth: header.getSize() }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length
                  ? table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        className="cursor-pointer h-15.5"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            style={{ maxWidth: cell.column.getSize() }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : null}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
